import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Layout,
  message,
  Radio,
  Row,
  Select,
  Typography,
} from "antd";
import { getData, postData, putData } from "apiServices/common";
import AppPageHeader from "common/PageHeader";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";

const MachineInformationForm = () => {
  const [configDropdown, setConfigDropdown] = useState({
    action_types: [],
    brands: [],
    countries: [],
    locations: [],
    machine_lines: [],
    models: [],
    sewing_lines: [],
    machine_companies: [],
    ob_machine_types: [],
  });
  const [sewingLineSelected, setSewingLineSelected] = useState(false);
  const [machineLineSelected, setMachineLineSelected] = useState(false);
  const [lineType, setLineType] = useState(null);

  //   Routes
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();

  const isView = pathname.includes("view");

  //   Antd Constants
  const [form] = Form.useForm();

  const extra = (
    <div className="extra-button-container">
      <Button
        size="small"
        htmlType="submit"
        style={{ marginRight: 5 }}
        onClick={() => {
          form.resetFields();
          navigate("/digitalization/configurations/machine-information");
        }}
      >
        {" "}
        Back
      </Button>
      {isView ? null : (
        <Button
          htmlType="submit"
          type="primary"
          size="small"
          onClick={() => form.submit()}
          style={{ marginRight: 5 }}
        >
          {" "}
          {id ? "Update" : "Save"}
        </Button>
      )}
    </div>
  );

  const fetchConfigData = useCallback(async () => {
    try {
      const response = await getData(
        `/api/digitalization/machine-maintenance/machine-information/pre-requisites`,
        null,
        {},
      );
      const data = response.data?.data;

      const cglMachineCompany = data.machine_companies.find(
        (line) => line.name.toLowerCase() === "cgl",
      );

      setConfigDropdown(data);

      if (!id) {
        form.setFieldsValue({
          ...data,
          machine_company_id: cglMachineCompany
            ? cglMachineCompany.id
            : undefined,
        });
      }
    } catch (error) {
      message.error("Failed to fetch machine information");
    }
  }, [form, id]);

  useEffect(() => {
    fetchConfigData();
  }, [fetchConfigData]);

  useEffect(() => {
    if (id) {
      const fetchDetails = async () => {
        try {
          const response = await getData(
            `/api/digitalization/machine-maintenance/machine-information/${id}`,
            null,
            {},
          );
          const data = response.data?.data;

          form.setFieldsValue({
            machine_number: data.machine_number,
            o_b_machine_type_id: data.o_b_machine_type?.id ?? null,
            machine_company_id: data.machine_company?.id ?? null,
            machine_name: data.machine_name,
            machine_action_type_id: data.machine_action_type?.id ?? null,
            machine_brand_id: data.machine_brand?.id ?? null,
            machine_model_id: data.machine_model?.id ?? null,
            machine_origin_country_id: data.machine_origin_country?.id ?? null,
            machine_location_id: data.machine_location?.id ?? null,
            sewing_line_id: data.sewing_line?.id ?? null,
            machine_line_id: data.machine_line?.id ?? null,
            invoice_no: data.invoice_no,
            invoice_year: data.invoice_year,
            machine_capacity: data.machine_capacity,
            machine_power: data.machine_power,
            ownership_type: data.ownership_type,
            installation_year: data.installation_year,
            status: data.status,
            line_type: data.sewing_line?.id
              ? "sewing"
              : data.machine_line?.id
              ? "machine"
              : null,
          });

          if (data.sewing_line?.id) {
            setLineType("sewing");
            setSewingLineSelected(true);
            setMachineLineSelected(false);
          } else if (data.machine_line?.id) {
            setLineType("machine");
            setMachineLineSelected(true);
            setSewingLineSelected(false);
          } else {
            setLineType(null);
            setSewingLineSelected(false);
            setMachineLineSelected(false);
          }
        } catch (error) {
          message.error("Failed to fetch details");
        }
      };
      fetchDetails();
    }
  }, [id, form]);

  const normalizePayload = (values) => {
    return {
      machine_number: values.machine_number,
      machine_name: values.machine_name,
      machine_action_type_id: values.machine_action_type_id ?? null,
      machine_brand_id: values.machine_brand_id ?? null,
      machine_model_id: values.machine_model_id ?? null,
      machine_origin_country_id: values.machine_origin_country_id ?? null,
      machine_location_id: values.machine_location_id ?? null,
      line_type: values.line_type,
      sewing_line_id:
        values.line_type === "sewing" ? values.sewing_line_id ?? null : null,
      machine_line_id:
        values.line_type === "machine" ? values.machine_line_id ?? null : null,
      invoice_no: values.invoice_no ?? null,
      invoice_year: values.invoice_year ?? null,
      machine_capacity: values.machine_capacity ?? null,
      machine_power: values.machine_power ?? null,
      ownership_type: values.ownership_type ?? null,
      installation_year: values.installation_year ?? null,
      status: values.status,
      machine_company_id: values.machine_company_id,
      o_b_machine_type_id: values.o_b_machine_type_id,
    };
  };

  const handleSubmit = async (values) => {
    const payload = normalizePayload(values);
    try {
      if (id) {
        const resp = await putData(
          `/api/digitalization/machine-maintenance/machine-information/${id}`,
          payload,
        );

        if (resp) {
          message.success("Machine information updated successfully");
          navigate("/digitalization/configurations/machine-information");
        }
      } else {
        const resp = await postData(
          `/api/digitalization/machine-maintenance/machine-information`,
          payload,
        );

        if (resp) {
          message.success("Machine information added successfully");
          navigate("/digitalization/configurations/machine-information");
        }
      }
    } catch (error) {
      message.error("Failed to submit machine information");
    }
  };

  return (
    <>
      <AppPageHeader
        title={
          <Typography.Title level={5}>
            {id ? "Update" : "Add"} Machine Information
          </Typography.Title>
        }
        extra={extra}
      />

      <Layout.Content className="item-details">
        <Card
          title="Entry Details"
          style={{ marginTop: 20, boxShadow: "none" }}
          bordered={false}
        >
          <Row gutter={16}>
            <Form
              form={form}
              layout="vertical"
              style={{ width: "100%" }}
              onFinish={handleSubmit}
              disabled={isView}
              initialValues={{
                status: 1,
              }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Machine Company"
                    name="machine_company_id"
                    rules={[
                      {
                        required: true,
                        message: "Machine Company is required",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select Machine Company"
                      optionFilterProp="children"
                      allowClear
                    >
                      {configDropdown.machine_companies?.map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Machine Type"
                    name="o_b_machine_type_id"
                    rules={[
                      { required: true, message: "Machine Type is required" },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select Machine Type"
                      optionFilterProp="children"
                      allowClear
                    >
                      {configDropdown.ob_machine_types?.map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Machine ID"
                    name="machine_number"
                    rules={[
                      { required: true, message: "Machine ID is required" },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Machine Name"
                    name="machine_name"
                    rules={[
                      { required: true, message: "Machine Name is required" },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Action Type"
                    name="machine_action_type_id"
                    rules={[
                      { required: false, message: "Action Type is required" },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select Action Type"
                      optionFilterProp="children"
                      allowClear
                    >
                      {configDropdown.action_types?.map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Brand"
                    name="machine_brand_id"
                    rules={[{ required: true, message: "Brand is required" }]}
                  >
                    <Select
                      showSearch
                      placeholder="Select Brand"
                      optionFilterProp="children"
                      allowClear
                    >
                      {configDropdown.brands?.map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Model"
                    name="machine_model_id"
                    rules={[{ required: false, message: "Model is required" }]}
                  >
                    <Select
                      showSearch
                      placeholder="Select Model"
                      optionFilterProp="children"
                      allowClear
                    >
                      {configDropdown.models?.map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Origin Country"
                    name="machine_origin_country_id"
                    rules={[
                      {
                        required: false,
                        message: "Origin Country is required",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select Country"
                      optionFilterProp="children"
                      allowClear
                    >
                      {configDropdown.countries?.map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Location"
                    name="machine_location_id"
                    rules={[
                      { required: true, message: "Location is required" },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select Location"
                      optionFilterProp="children"
                      allowClear
                    >
                      {configDropdown.locations?.map((item) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Line Type"
                    name="line_type"
                    rules={[
                      { required: true, message: "Line Type is required" },
                    ]}
                  >
                    <Select
                      placeholder="Select Line Type"
                      allowClear
                      disabled={isView}
                      onChange={(value) => {
                        setLineType(value);
                        form.setFieldsValue({
                          sewing_line_id: null,
                          machine_line_id: null,
                        });
                        setSewingLineSelected(false);
                        setMachineLineSelected(false);
                      }}
                    >
                      <Select.Option value="sewing">Sewing</Select.Option>
                      <Select.Option value="machine">Machine</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                {/* Sewing Line - only visible if lineType is 'sewing' */}
                {lineType === "sewing" && (
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      label="Sewing Line"
                      name="sewing_line_id"
                      rules={[
                        { required: true, message: "Sewing Line is required" },
                      ]}
                    >
                      <Select
                        showSearch
                        placeholder="Select Sewing Line"
                        optionFilterProp="children"
                        allowClear
                        disabled={isView}
                        onChange={(value) => {
                          setSewingLineSelected(!!value);
                          if (value) {
                            form.setFieldsValue({ machine_line_id: null });
                            setMachineLineSelected(false);
                          }
                        }}
                        onClear={() => setSewingLineSelected(false)}
                      >
                        {configDropdown.sewing_lines?.map((item) => (
                          <Select.Option key={item.id} value={item.id}>
                            {item.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                {/* Machine Line - only visible if lineType is 'machine' */}
                {lineType === "machine" && (
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      label="Machine Line"
                      name="machine_line_id"
                      rules={[
                        { required: true, message: "Machine Line is required" },
                      ]}
                    >
                      <Select
                        showSearch
                        placeholder="Select Machine Line"
                        optionFilterProp="children"
                        allowClear
                        disabled={isView}
                        onChange={(value) => {
                          setMachineLineSelected(!!value);
                          if (value) {
                            form.setFieldsValue({ sewing_line_id: null });
                            setSewingLineSelected(false);
                          }
                        }}
                        onClear={() => setMachineLineSelected(false)}
                      >
                        {configDropdown.machine_lines?.map((item) => (
                          <Select.Option key={item.id} value={item.id}>
                            {item.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Invoice No"
                    name="invoice_no"
                    rules={[
                      { required: false, message: "Invoice No is required" },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Invoice Year"
                    name="invoice_year"
                    rules={[
                      { required: false, message: "Invoice Year is required" },
                    ]}
                  >
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Installation Year"
                    name="installation_year"
                    rules={[
                      {
                        required: false,
                        message: "Installation Year is required",
                      },
                    ]}
                  >
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Machine Capacity"
                    name="machine_capacity"
                    rules={[{ required: false }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    label="Machine Power"
                    name="machine_power"
                    rules={[{ required: false }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>

                {/* Ownership Type and Status together */}
                <Col xs={24} sm={12} md={6} style={{ marginTop: 10 }}>
                  <Form.Item
                    label="Ownership Type"
                    name="ownership_type"
                    rules={[
                      { required: true, message: "Ownership Type is required" },
                    ]}
                  >
                    <Radio.Group>
                      <Radio value="Own">Own</Radio>
                      <Radio value="Rented">Rented</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6} style={{ marginTop: 10 }}>
                  <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: true, message: "Status is required" }]}
                  >
                    <Radio.Group>
                      <Radio value={1}>Active</Radio>
                      <Radio value={0}>Inactive</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Row>
        </Card>
      </Layout.Content>
    </>
  );
};

export default MachineInformationForm;
